/**
 * @file The ambient grid dynamic actors are lit by. Static geometry gets its
 * light baked into vertices; the player, the enemies and instanced props cannot,
 * so they sample this instead — one bilinear lookup per actor per frame, and
 * they darken in alleys, brighten in plazas and glow near lit windows at
 * essentially zero runtime cost.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT A PROBE STORES
 * ─────────────────────────────────────────────────────────────────────────────
 * Two RGB triples — day and night — mixed on the CPU by `uNight` at sample time
 * (the same two-channel idea as the vertex bake, one level up). A probe has no
 * surface normal, so:
 *   • the sun term is scaled by `cfg.probeSunScale` instead of `max(N.L,0)`
 *     (~the average over a standing actor), and
 *   • emitters are gathered OMNIDIRECTIONALLY (`gather` with a zero normal).
 * Sky visibility is the same cosine-hemisphere AO the vertex bake uses, around
 * +Y — that is what makes an alley probe dark.
 *
 * ⚠ API NOTE. `types.js -> LightProbeGrid` (the contract T5 codes against)
 * specifies `sample(x, y, z, nightAmt, out)`, so THAT is what {@link LightProbeGrid#sample}
 * is. The raw two-channel form from the T4 brief lives on as
 * {@link LightProbeGrid#sampleDayNight} `(x, z, outDay, outNight)`.
 *
 * @module engine/bake/LightProbeGrid
 */

import { DAY_DEFAULTS, mergeConfig } from './BakeConfig.js';
import { sampleAO } from './bakeVertexLight.js';

/** @typedef {import('../types.js').Bounds2} Bounds2 */
/** @typedef {import('./Occluders.js').OccluderSet} OccluderSet */
/** @typedef {import('./Emitters.js').EmitterSet} EmitterSet */

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * @param {number} x @param {number} y @param {number} z
 * @returns {number} uint32
 */
function hash3(x, y, z) {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(z | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  return (h ^ (h >>> 13)) >>> 0;
}

export class LightProbeGrid {
  /**
   * Prefer {@link LightProbeGrid.build}. The constructor only wires fields.
   * @param {{origin: {x:number,y:number,z:number}, cell: number, dims: {x:number,y:number,z:number}, day: Float32Array, night: Float32Array, layerH?: number, ms?: number}} o
   */
  constructor(o) {
    /** @type {{x:number,y:number,z:number}} World position of probe (0,0,0). */
    this.origin = o.origin;
    /** @type {number} Horizontal spacing, metres. */
    this.cell = o.cell;
    /** @type {{x:number,y:number,z:number}} Probe counts per axis. */
    this.dims = o.dims;
    /** @type {number} Vertical spacing between layers (1 layer => unused). */
    this.layerH = o.layerH ?? 0;
    /** @type {Float32Array} RGB triples, `dims.x*dims.y*dims.z*3`. */
    this.day = o.day;
    /** @type {Float32Array} Same layout. */
    this.night = o.night;
    /** @type {number} Build time, milliseconds. */
    this.ms = o.ms ?? 0;

    this._d = new Float32Array(3);
    this._n = new Float32Array(3);
  }

  /** @returns {number} Total probe count. */
  get count() {
    return this.dims.x * this.dims.y * this.dims.z;
  }

  /**
   * Solves a grid of probes over `bounds`.
   *
   * @param {Object} o
   * @param {Bounds2} o.bounds Playable extent (`GeoBuilder.bounds()` works).
   * @param {number} [o.cell=2] Horizontal spacing, metres.
   * @param {OccluderSet|null} [o.occ]
   * @param {EmitterSet|null} [o.emitters]
   * @param {Partial<import('../types.js').BakeConfig>|null} [o.cfg]
   * @param {number} [o.y=1.2] Sample height — chest height on a 1.6 m actor.
   * @param {number} [o.layers=1] >1 stacks layers upward from `y`, spaced
   *   `layerH`, and {@link LightProbeGrid#sample} interpolates in Y too
   *   (rooftops / overpasses).
   * @param {number} [o.layerH=2.5]
   * @param {(t: number) => void} [o.onProgress]
   * @returns {LightProbeGrid}
   */
  static build({ bounds, cell = 2, occ = null, emitters = null, cfg = null, y = 1.2, layers = 1, layerH = 2.5, onProgress = null }) {
    const t0 = now();
    const config = mergeConfig(DAY_DEFAULTS, cfg);
    const c = Math.max(0.25, cell);
    const minX = bounds?.minX ?? 0;
    const minZ = bounds?.minZ ?? 0;
    const spanX = Math.max(0, (bounds?.maxX ?? 0) - minX);
    const spanZ = Math.max(0, (bounds?.maxZ ?? 0) - minZ);
    const nx = Math.max(1, Math.floor(spanX / c) + 1);
    const nz = Math.max(1, Math.floor(spanZ / c) + 1);
    const ny = Math.max(1, Math.round(layers));

    const day = new Float32Array(nx * ny * nz * 3);
    const night = new Float32Array(nx * ny * nz * 3);
    const d3 = new Float32Array(3);
    const n3 = new Float32Array(3);

    let done = 0;
    const total = nx * ny * nz;
    for (let iy = 0; iy < ny; iy++) {
      const py = y + iy * layerH;
      for (let iz = 0; iz < nz; iz++) {
        const pz = minZ + iz * c;
        for (let ix = 0; ix < nx; ix++) {
          const px = minX + ix * c;
          solveProbe(px, py, pz, occ, emitters, config, d3, n3);
          const o3 = ((iy * nz + iz) * nx + ix) * 3;
          day[o3] = d3[0];
          day[o3 + 1] = d3[1];
          day[o3 + 2] = d3[2];
          night[o3] = n3[0];
          night[o3 + 1] = n3[1];
          night[o3 + 2] = n3[2];
          done++;
        }
      }
      onProgress?.(done / total);
    }

    return new LightProbeGrid({
      origin: { x: minX, y, z: minZ },
      cell: c,
      dims: { x: nx, y: ny, z: nz },
      layerH,
      day,
      night,
      ms: now() - t0,
    });
  }

  /**
   * Raw two-channel sample (bilinear in XZ on the bottom layer, which is the one
   * ground actors stand in). Levels that pre-mix on the CPU want this; anything
   * that just wants a tint wants {@link LightProbeGrid#sample}.
   *
   * @param {number} x @param {number} z
   * @param {Float32Array|number[]|{r:number,g:number,b:number}} [outDay]
   * @param {Float32Array|number[]|{r:number,g:number,b:number}} [outNight]
   * @returns {{day: any, night: any}}
   */
  sampleDayNight(x, z, outDay, outNight) {
    const d = outDay ?? this._d;
    const n = outNight ?? this._n;
    this._lerp(x, this.origin.y, z, d, n);
    return { day: d, night: n };
  }

  /**
   * THE `types.js` CONTRACT. Trilinearly samples both channels and writes the
   * `nightAmt` mix into `out` (a `THREE.Color`, or any `{r,g,b}`).
   *
   * @param {number} x @param {number} y @param {number} z
   * @param {number} nightAmt 0 = full day, 1 = full night (`uNight.value`).
   * @param {{r:number,g:number,b:number}} [out]
   * @returns {{r:number,g:number,b:number}} `out`
   */
  sample(x, y, z, nightAmt, out) {
    const o = out ?? { r: 0, g: 0, b: 0 };
    const d = this._d;
    const n = this._n;
    this._lerp(x, y, z, d, n);
    const t = nightAmt < 0 ? 0 : nightAmt > 1 ? 1 : nightAmt;
    o.r = d[0] + (n[0] - d[0]) * t;
    o.g = d[1] + (n[1] - d[1]) * t;
    o.b = d[2] + (n[2] - d[2]) * t;
    return o;
  }

  /**
   * Trilinear fetch into two RGB outputs. Coordinates outside the grid clamp to
   * the edge probe (no wrap, no black border).
   * @param {number} x @param {number} y @param {number} z
   * @param {any} outD @param {any} outN
   */
  _lerp(x, y, z, outD, outN) {
    const { x: nx, y: ny, z: nz } = this.dims;
    const c = this.cell;

    let fx = (x - this.origin.x) / c;
    let fz = (z - this.origin.z) / c;
    let fy = ny > 1 ? (y - this.origin.y) / (this.layerH || 1) : 0;
    fx = fx < 0 ? 0 : fx > nx - 1 ? nx - 1 : fx;
    fz = fz < 0 ? 0 : fz > nz - 1 ? nz - 1 : fz;
    fy = fy < 0 ? 0 : fy > ny - 1 ? ny - 1 : fy;

    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const iy = Math.floor(fy);
    const tx = fx - ix;
    const tz = fz - iz;
    const ty = fy - iy;
    const ix1 = ix + 1 < nx ? ix + 1 : ix;
    const iz1 = iz + 1 < nz ? iz + 1 : iz;
    const iy1 = iy + 1 < ny ? iy + 1 : iy;

    const D = this.day;
    const N = this.night;
    const idx = (jx, jy, jz) => ((jy * nz + jz) * nx + jx) * 3;
    const a = idx(ix, iy, iz);
    const b = idx(ix1, iy, iz);
    const cI = idx(ix, iy, iz1);
    const d = idx(ix1, iy, iz1);
    const e = idx(ix, iy1, iz);
    const f = idx(ix1, iy1, iz);
    const g = idx(ix, iy1, iz1);
    const h = idx(ix1, iy1, iz1);

    for (let k = 0; k < 3; k++) {
      const d00 = D[a + k] + (D[b + k] - D[a + k]) * tx;
      const d01 = D[cI + k] + (D[d + k] - D[cI + k]) * tx;
      const d10 = D[e + k] + (D[f + k] - D[e + k]) * tx;
      const d11 = D[g + k] + (D[h + k] - D[g + k]) * tx;
      const dz0 = d00 + (d01 - d00) * tz;
      const dz1 = d10 + (d11 - d10) * tz;
      outD[k] = dz0 + (dz1 - dz0) * ty;

      const n00 = N[a + k] + (N[b + k] - N[a + k]) * tx;
      const n01 = N[cI + k] + (N[d + k] - N[cI + k]) * tx;
      const n10 = N[e + k] + (N[f + k] - N[e + k]) * tx;
      const n11 = N[g + k] + (N[h + k] - N[g + k]) * tx;
      const nz0 = n00 + (n01 - n00) * tz;
      const nz1 = n10 + (n11 - n10) * tz;
      outN[k] = nz0 + (nz1 - nz0) * ty;
    }
  }

  /**
   * World position of one probe — the dev scene's marker cubes use it.
   * @param {number} ix @param {number} iy @param {number} iz
   * @returns {{x:number,y:number,z:number}}
   */
  probePos(ix, iy, iz) {
    return {
      x: this.origin.x + ix * this.cell,
      y: this.origin.y + iy * this.layerH,
      z: this.origin.z + iz * this.cell,
    };
  }

  /** Typed arrays are plain memory; nothing GPU-side to release. */
  dispose() {
    /* no-op — kept so `Level.probes` matches the DecalLayer/Level dispose shape */
  }
}

/**
 * One probe. Exported because the enemy/prop instancers want the SAME numbers
 * at an arbitrary point without building a whole grid (`iColD`/`iColN` in
 * `build/InstancedBatch.js`).
 *
 * @param {number} px @param {number} py @param {number} pz
 * @param {OccluderSet|null} occ
 * @param {EmitterSet|null} emitters
 * @param {any} cfg Merged config.
 * @param {Float32Array|number[]} outDay
 * @param {Float32Array|number[]} outNight
 */
/**
 * Presents SEVERAL grids as one, dispatching each sample to the grid that
 * covers it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NOT ONE BIGGER GRID
 * ─────────────────────────────────────────────────────────────────────────────
 * A grid is `O(area / cell^2)` to solve AND to hold, and a level with two towns
 * has open sea between them. One grid spanning both would spend the great
 * majority of its probes describing water — at 2.8 m over a 2 km square that is
 * ~510,000 probes against the ~36,000 the two towns actually need, each one a
 * full AO + sun + emitter solve on the main thread during loading. It is the
 * difference between a fifth of a second and several minutes.
 *
 * Every grid CLAMPS outside its own bounds, so the dispatch only has to be
 * right about which town a point belongs to; a point out in the countryside
 * gets the nearest town's edge probe, which is the open-sky value it would have
 * solved for anyway. That is the same argument that justified bounding the
 * single grid to the plateau in the first place.
 *
 * @param {LightProbeGrid[]} grids
 * @returns {any} One grid when given one — no wrapper on the common path.
 */
export function combineProbeGrids(grids) {
  const list = grids.filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];

  // Bounds and centre per grid, precomputed: `sample` is called per actor per
  // frame and must not be doing arithmetic on `dims` every time.
  const box = list.map((g) => {
    const w = (g.dims.x - 1) * g.cell;
    const d = (g.dims.z - 1) * g.cell;
    return { minX: g.origin.x, minZ: g.origin.z, maxX: g.origin.x + w, maxZ: g.origin.z + d, cx: g.origin.x + w / 2, cz: g.origin.z + d / 2 };
  });

  /** @param {number} x @param {number} z @returns {LightProbeGrid} */
  const pick = (x, z) => {
    for (let i = 0; i < box.length; i++) {
      const b = box[i];
      if (x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ) return list[i];
    }
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < box.length; i++) {
      const b = box[i];
      const dx = x - b.cx;
      const dz = z - b.cz;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return list[best];
  };

  let count = 0;
  let ms = 0;
  for (const g of list) {
    count += g.count ?? 0;
    ms += g.ms ?? 0;
  }

  return {
    grids: list,
    count,
    ms,
    cell: list[0].cell,
    dims: list[0].dims,
    origin: list[0].origin,
    pick,
    sample: (x, y, z, nightAmt, out) => pick(x, z).sample(x, y, z, nightAmt, out),
    sampleDayNight: (x, z, outDay, outNight) => pick(x, z).sampleDayNight(x, z, outDay, outNight),
    dispose() {
      for (const g of list) g.dispose?.();
    },
  };
}

export function solveProbe(px, py, pz, occ, emitters, cfg, outDay, outNight) {
  // Buried in geometry: no sky, no sun, just the floors. Without this test a
  // probe inside a building reads as OPEN SKY (rays ignore the box they start
  // in) and bleeds light into the alley next door through the bilinear filter.
  if (occ && occ.count > 0 && occ.contains(px, py, pz)) {
    for (let c = 0; c < 3; c++) {
      outDay[c] = cfg.ambientFloor + cfg.hemiGround[c] * cfg.hemiIntensity * cfg.ao.floor;
      outNight[c] = cfg.nightFloor + cfg.nightHemi[c] * cfg.nightHemiIntensity * cfg.ao.floor;
    }
    return;
  }

  const hash = hash3(Math.round(px * 8), Math.round(py * 8), Math.round(pz * 8));
  const sky = sampleAO(occ, px, py, pz, 0, 1, 0, cfg, hash);

  const L = cfg.sunDir;
  const M = cfg.moonDir;
  const sunVis = occ && occ.count > 0
    ? occ.sunVis(px, py, pz, L, {
      rays: cfg.sunRays,
      cone: (cfg.sunConeDeg * Math.PI) / 180,
      maxT: cfg.sunMaxDist,
      rng: null,
    })
    : 1;
  const moonVis = occ && occ.count > 0 && cfg.moonRays > 0
    ? occ.sunVis(px, py, pz, M, {
      rays: cfg.moonRays,
      cone: (cfg.sunConeDeg * Math.PI) / 180,
      maxT: cfg.sunMaxDist,
      rng: null,
    })
    : 1;

  const emit = outNight; // scratch, overwritten below
  if (emitters && emitters.count > 0) emitters.gather(px, py, pz, 0, 0, 0, emit);
  else {
    emit[0] = 0;
    emit[1] = 0;
    emit[2] = 0;
  }
  const e0 = emit[0];
  const e1 = emit[1];
  const e2 = emit[2];

  const sunI = cfg.sunIntensity * sunVis * cfg.probeSunScale;
  const moonI = cfg.moonIntensity * moonVis * cfg.moonVisScale * cfg.probeSunScale;

  for (let c = 0; c < 3; c++) {
    const e = c === 0 ? e0 : c === 1 ? e1 : e2;
    // Probes see the sky hemisphere, so the hemi term uses hemiSky directly
    // rather than the normal-tilted blend the vertex bake uses.
    outDay[c] =
      cfg.sunColor[c] * sunI +
      cfg.hemiSky[c] * cfg.hemiIntensity * sky +
      cfg.bounceColor[c] * cfg.bounceIntensity * sky +
      cfg.ambientFloor;
    outNight[c] =
      cfg.moonColor[c] * moonI +
      cfg.nightHemi[c] * cfg.nightHemiIntensity * sky +
      e * cfg.emitterIntensity +
      cfg.nightFloor;
  }
}
